import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, ExternalLink, Link as LinkIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { documentCaptureService } from '../../services/documentCaptureService';
import { useReviewDecision } from '../../hooks/useReviewDecision';
import { DocReviewReason, type ReviewQueueItem } from '../../types/documentCapture.types';

/**
 * "Link it in NetSuite yourself" (86d45ffw4). NetSuite refused the write — closed period, voided
 * transaction, or a rejected PATCH — so the scan sits in DOP unlinked. A person with a NetSuite login
 * can paste the link on the record in ten seconds; this is where they do it and say so.
 *
 * <p>DOP writes NOTHING to NetSuite from here. Pressing confirm records a CLAIM, and the nightly
 * reconciliation sweep is what turns it into a plain Linked once it sees the token on the record.
 * The copy says exactly that, because the alternative is a green chip that means "somebody said so".</p>
 */
interface ManualLinkCardProps {
  item: ReviewQueueItem;
}

/** The reason the write failed, said once, as the card's header. */
function whyLine(reason: DocReviewReason): string {
  if (reason === DocReviewReason.ClosedPeriod)
    return 'A closed period doesn’t stop you pasting the link on the record yourself.';
  if (reason === DocReviewReason.NsVoided)
    return 'The transaction is voided, but you can still paste the link on it yourself.';
  return 'DOP can’t write to this record, but you can.';
}

export function ManualLinkCard({ item }: ManualLinkCardProps) {
  const { submit, submitting } = useReviewDecision();
  const [scanUrl, setScanUrl] = useState<string | null>(null);
  const [nsUrl, setNsUrl] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'done' | 'error'>('loading');
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
    Fetched on MOUNT, never on click. The URL has to be sitting in state before the reviewer presses
    Copy: an await between the click and `navigator.clipboard.writeText` loses the user-gesture
    context in Safari and the copy silently fails — the same reasoning that put `shareUrl` on the
    document payload in the first place (T10). It also means a capability token is minted only for
    documents somebody actually opened, rather than for every row of the NetSuite tab.
  */
  const load = useCallback(async () => {
    if (!item.docNumber) return;
    setState('loading');
    try {
      const doc = await documentCaptureService.getDocument(item.docNumber);
      setScanUrl(doc.shareUrl);
      setNsUrl(doc.netSuiteUrl);
      setState('done');
    } catch {
      setState('error');
    }
  }, [item.docNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const copy = useCallback(() => {
    if (!scanUrl) return;
    // Feedback first, clipboard second: the tick must not wait on a promise.
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
    void navigator.clipboard?.writeText(scanUrl).catch(() => {});
  }, [scanUrl]);

  const confirm = useCallback(() => {
    void submit(
      () => documentCaptureService.confirmManualLink(item.id, item.linkEpoch ?? 0),
      { successMessage: 'Saved — DOP will confirm it on the record' }
    );
  }, [submit, item.id, item.linkEpoch]);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-info/30 bg-info/5 p-3">
      <div className="flex items-start gap-2">
        <LinkIcon className="mt-px h-4 w-4 flex-shrink-0 text-info" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Link it in NetSuite yourself</p>
          {/* The WHY only. The two steps below say what to do; repeating it here made the header
              restate the form underneath it. */}
          <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground text-pretty">
            {whyLine(item.reason)}
          </p>
        </div>
      </div>

      {/*
        14ykpqtwauu — a journal un-applies its credit lines whenever it is saved, and pasting this link
        on the record IS a save. DOP's own write checks first and holds; a person pasting by hand gets
        no such check, so they get the warning instead.
      */}
      {item.effectivePrefix === 'JV' && (
        <p className="rounded-md border border-warning/30 bg-warning/10 px-2.5 py-2 text-[13px] leading-snug text-foreground text-pretty">
          <span className="font-semibold text-warning">Journal: </span>
          saving it in NetSuite — including pasting this link — un-applies any applied credit lines. Check
          its applications first, and re-apply them after you save.
        </p>
      )}

      {/* ---- step 1: the link ---- */}
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-info/20 text-[10.5px] font-bold text-info"
        >
          1
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <label htmlFor="ns-scan-url" className="block text-[13px] leading-snug text-foreground text-pretty">
            Paste this link into the Scan URL field on the transaction.
          </label>

          {state === 'loading' && <Skeleton className="h-8 w-full" />}

          {state === 'error' && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[13px] text-destructive">Couldn’t load this document’s link.</p>
              <Button variant="outline" size="sm" className="h-7" onClick={() => void load()}>
                Try again
              </Button>
            </div>
          )}

          {state === 'done' && !scanUrl && (
            <p className="text-[13px] text-muted-foreground">
              This document has no link yet. Reload the queue in a moment and it will be here.
            </p>
          )}

          {/*
            The design tiered these three labels off a measured row width (ResizeObserver, three
            breakpoints) so the link field never dropped below ~150px. Stacking the field above its
            buttons reaches the same goal with no measurement and no container-query dependency: the
            field is full-width at EVERY size, and the two buttons keep their words instead of
            degrading to bare icons a new reviewer has to hover to identify.
          */}
          {state === 'done' && scanUrl && (
            <div className="flex flex-col gap-1.5">
              <Input
                id="ns-scan-url"
                readOnly
                value={scanUrl}
                onFocus={(e) => e.currentTarget.select()}
                title="This link is permanent — anyone in NetSuite can open the scan from it"
                className="h-8 w-full font-mono text-[11.5px]"
              />
              {/*
                Two deliberate shapes here, both learned the hard way in a real render.

                ① The NetSuite link is a styled <a>, NOT <Button asChild>. This project's Button
                  renders `{isLoading && <Loader2/>}` beside `{children}`, so Slot receives TWO
                  children (`false` and the anchor) and throws "React.Children.only expected to
                  receive a single React element child" — blanking the entire page. Same reason, and
                  same fix, as QuotationMasterList.tsx:1119.

                ② No Tooltip on either control. UI-GUIDELINES §5 requires one on ICON-ONLY controls;
                  both of these carry a visible text label, so a tooltip would only restate it.
              */}
              <div className="flex flex-wrap items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copy}
                  aria-label="Copy the link"
                  className={cn('h-8 gap-1.5', copied && 'border-success/45 text-success')}
                >
                  {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                {nsUrl && (
                  <a
                    href={nsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${item.docNumber} in NetSuite`}
                    className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'h-8 gap-1.5 no-underline')}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    Open in NetSuite
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---- step 2: tell DOP ---- */}
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-info/20 text-[10.5px] font-bold text-info"
        >
          2
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="text-[13px] leading-snug text-foreground text-pretty">Come back and tell DOP it’s done.</p>
          <Button
            size="sm"
            className="h-8 self-start"
            disabled={submitting || !scanUrl}
            onClick={confirm}
            aria-label="I've pasted the link in NetSuite"
          >
            {submitting ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Check className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            I’ve pasted the link in NetSuite
          </Button>
          <p className="text-xs leading-snug text-muted-foreground text-pretty">
            Saves your name and the time. DOP checks the record nightly and switches this to a plain
            Linked once it sees the link there.
          </p>
        </div>
      </div>
    </div>
  );
}
